/* ==========================================================================
 * Shared admin UI primitives.
 *
 * Presentation only — none of these components fetch, mutate, or authorise
 * anything. They exist so the admin modules stop hand-rolling one-off card,
 * button and pill markup, and so every surface reads from the same tokens
 * defined in src/styles/admin-theme.css.
 * ========================================================================== */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

/* -- layout ---------------------------------------------------------------- */

export const Card = ({ className = '', children, ...rest }) => (
  <div
    className={`bg-adm-bg-1 rounded-adm-card shadow-adm-card ${className}`}
    {...rest}
  >
    {children}
  </div>
);

export const CardHeader = ({ title, description, actions, className = '' }) => (
  <div
    className={`flex items-start justify-between gap-4 px-5 py-4 border-b border-adm-border ${className}`}
  >
    <div className="min-w-0">
      <h2 className="text-[15px] font-semibold text-adm-text-hi truncate">{title}</h2>
      {description && (
        <p className="mt-0.5 text-xs text-adm-text-lo">{description}</p>
      )}
    </div>
    {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
  </div>
);

export const PageHeader = ({ title, description, actions, className = '' }) => (
  <div className={`flex flex-wrap items-end justify-between gap-4 mb-5 ${className}`}>
    <div className="min-w-0">
      <h1 className="text-[22px] font-semibold tracking-[-0.01em] text-adm-text-hi">
        {title}
      </h1>
      {description && (
        <p className="mt-1 text-[13px] text-adm-text-mid">{description}</p>
      )}
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

export const SectionTitle = ({ children, className = '' }) => (
  <h3 className={`text-[15px] font-semibold text-adm-text-hi ${className}`}>
    {children}
  </h3>
);

export const Toolbar = ({ children, className = '' }) => (
  <div className={`flex flex-wrap items-center gap-2 mb-4 ${className}`}>{children}</div>
);

/* -- buttons --------------------------------------------------------------- */

const BUTTON_VARIANTS = {
  primary:
    'bg-adm-blue text-white hover:bg-adm-blue-hover active:bg-adm-blue-active',
  secondary:
    'bg-adm-bg-2 text-adm-text-hi shadow-[inset_0_0_0_1px_oklch(var(--adm-border))] hover:bg-adm-bg-3',
  ghost: 'text-adm-text-mid hover:bg-adm-bg-3 hover:text-adm-text-hi',
  danger:
    'text-adm-danger shadow-[inset_0_0_0_1px_oklch(var(--adm-danger)/0.45)] hover:bg-adm-danger/10',
  destructive: 'bg-adm-danger text-white hover:bg-adm-danger/85',
  accent: 'bg-adm-accent text-adm-bg-0 hover:bg-adm-accent/85',
};

const BUTTON_SIZES = {
  sm: 'h-7 px-2.5 text-xs',
  md: 'h-8 px-3 text-[13px]',
  lg: 'h-9 px-4 text-[13px]',
};

export const Button = ({
  variant = 'secondary',
  size = 'md',
  icon: Icon,
  className = '',
  children,
  ...rest
}) => (
  <button
    type="button"
    className={`inline-flex items-center justify-center gap-1.5 rounded-adm-ctl font-medium
      transition-colors duration-150 disabled:opacity-45 disabled:cursor-not-allowed
      ${BUTTON_SIZES[size] || BUTTON_SIZES.md} ${BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.secondary} ${className}`}
    {...rest}
  >
    {Icon && <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
    {children}
  </button>
);

/* -- status pills ---------------------------------------------------------- */

const PILL_TONES = {
  success: 'bg-[oklch(var(--adm-pill-success-bg))] text-[oklch(var(--adm-pill-success-fg))] ring-[oklch(var(--adm-success)/0.35)]',
  danger: 'bg-[oklch(var(--adm-pill-danger-bg))] text-[oklch(var(--adm-pill-danger-fg))] ring-[oklch(var(--adm-danger)/0.35)]',
  warning: 'bg-[oklch(var(--adm-pill-accent-bg))] text-[oklch(var(--adm-pill-accent-fg))] ring-[oklch(var(--adm-accent)/0.35)]',
  info: 'bg-[oklch(var(--adm-pill-blue-bg))] text-[oklch(var(--adm-pill-blue-fg))] ring-[oklch(var(--adm-blue)/0.35)]',
  neutral: 'bg-[oklch(var(--adm-pill-neutral-bg))] text-[oklch(var(--adm-pill-neutral-fg))] ring-[oklch(var(--adm-border-strong))]',
};

/** Maps the status strings the dealership modules already emit onto a tone. */
export const toneForStatus = (status) => {
  const s = String(status || '').toLowerCase();
  if (/paid|approved|active|delivered|complete|completed|success|sold|verified|signed|current|available/.test(s)) return 'success';
  if (/fail|failed|declin|overdue|late|error|cancel|void|reject|delinquen|expired|denied/.test(s)) return 'danger';
  if (/pend|review|expiring|awaiting|hold|partial|processing|in.?progress|scheduled|draft/.test(s)) return 'warning';
  if (/new|open|sent|contacted|quoted|submitted/.test(s)) return 'info';
  return 'neutral';
};

export const StatusPill = ({ status, tone, children, className = '' }) => {
  const resolved = tone || toneForStatus(status ?? children);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium
        ring-1 whitespace-nowrap ${PILL_TONES[resolved] || PILL_TONES.neutral} ${className}`}
    >
      {children ?? status}
    </span>
  );
};

/* -- form controls --------------------------------------------------------- */

export const Field = ({ label, hint, htmlFor, required, className = '', children }) => (
  <div className={className}>
    {label && (
      <label
        htmlFor={htmlFor}
        className="block mb-1 text-xs font-medium text-adm-text-mid"
      >
        {label}
        {required && <span className="text-adm-danger ml-0.5">*</span>}
      </label>
    )}
    {children}
    {hint && <p className="mt-1 text-xs text-adm-text-lo">{hint}</p>}
  </div>
);

const CONTROL_CLASS =
  'w-full bg-adm-bg-2 text-adm-text-hi text-[13px] rounded-adm-ctl px-2.5 py-1.5 ' +
  'border border-adm-border-strong placeholder:text-adm-text-lo ' +
  'focus:border-adm-blue transition-colors duration-150';

export const Input = ({ className = '', ...rest }) => (
  <input className={`${CONTROL_CLASS} ${className}`} {...rest} />
);

export const Select = ({ className = '', children, ...rest }) => (
  <select className={`${CONTROL_CLASS} ${className}`} {...rest}>
    {children}
  </select>
);

export const Textarea = ({ className = '', ...rest }) => (
  <textarea className={`${CONTROL_CLASS} ${className}`} {...rest} />
);

/* -- KPI tiles ------------------------------------------------------------- */

/**
 * `delta` is rendered only when supplied by the caller — no metric is
 * synthesised here.
 */
export const KpiTile = ({ label, value, delta, deltaDirection, hint, icon: Icon, accent = false }) => {
  const dir = deltaDirection || (typeof delta === 'number' ? (delta >= 0 ? 'up' : 'down') : null);
  return (
    <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-medium text-adm-text-mid">{label}</p>
        {Icon && (
          <Icon
            className={`w-4 h-4 shrink-0 ${accent ? 'text-adm-accent' : 'text-adm-text-lo'}`}
            aria-hidden="true"
          />
        )}
      </div>
      <p className="mt-2 text-[26px] leading-none font-semibold text-adm-text-hi tabular-nums">
        {value}
      </p>
      {(delta !== undefined && delta !== null) && (
        <p
          className={`mt-2 inline-flex items-center gap-1 text-xs font-medium tabular-nums ${
            dir === 'down' ? 'text-adm-danger' : 'text-adm-success'
          }`}
        >
          <span aria-hidden="true">{dir === 'down' ? '▼' : '▲'}</span>
          {typeof delta === 'number' ? `${Math.abs(delta)}` : delta}
        </p>
      )}
      {hint && <p className="mt-2 text-xs text-adm-text-lo">{hint}</p>}
    </div>
  );
};

/* -- skeletons & empty states ---------------------------------------------- */

export const Skeleton = ({ className = '' }) => (
  <div className={`adm-skeleton ${className}`} />
);

export const SkeletonTable = ({ rows = 6, cols = 5 }) => (
  <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card overflow-hidden">
    <div className="flex gap-4 px-4 py-3 bg-adm-bg-2 border-b border-adm-border">
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton key={i} className="h-3 flex-1" />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="flex gap-4 px-4 h-10 items-center border-b border-adm-border last:border-b-0">
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton key={c} className="h-3 flex-1" />
        ))}
      </div>
    ))}
  </div>
);

export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center text-center py-14 px-6">
    {Icon && (
      <div className="w-10 h-10 rounded-adm-card bg-adm-bg-2 flex items-center justify-center mb-3">
        <Icon className="w-5 h-5 text-adm-text-lo" aria-hidden="true" />
      </div>
    )}
    <p className="text-[15px] font-semibold text-adm-text-hi">{title}</p>
    {description && (
      <p className="mt-1 text-[13px] text-adm-text-mid max-w-sm">{description}</p>
    )}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

/* -- stepper (visual only) -------------------------------------------------- */

export const Stepper = ({ steps = [], current = 0, onStepClick }) => (
  <ol className="flex flex-wrap items-center gap-x-2 gap-y-3">
    {steps.map((step, i) => {
      const label = typeof step === 'string' ? step : step.label;
      const done = i < current;
      const active = i === current;
      const Node = onStepClick ? 'button' : 'div';
      return (
        <li key={label ?? i} className="flex items-center gap-2">
          <Node
            {...(onStepClick
              ? { type: 'button', onClick: () => onStepClick(i), 'aria-current': active ? 'step' : undefined }
              : {})}
            className="flex items-center gap-2 rounded-adm-ctl px-1 py-0.5"
          >
            <span
              className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold tabular-nums transition-colors
                ${
                  done
                    ? 'bg-adm-blue text-white'
                    : active
                    ? 'bg-adm-blue-tint text-adm-text-hi ring-2 ring-adm-blue'
                    : 'bg-adm-bg-2 text-adm-text-lo'
                }`}
            >
              {done ? '✓' : i + 1}
            </span>
            <span
              className={`text-[13px] whitespace-nowrap ${
                active
                  ? 'text-adm-text-hi font-medium'
                  : done
                  ? 'text-adm-text-mid'
                  : 'text-adm-text-lo'
              }`}
            >
              {label}
            </span>
          </Node>
          {i < steps.length - 1 && (
            <span className="w-6 h-px bg-adm-border" aria-hidden="true" />
          )}
        </li>
      );
    })}
  </ol>
);

/* -- motion ---------------------------------------------------------------- */

/** 160ms ease-out fade + 4px rise. Honours prefers-reduced-motion via CSS. */
export const TabTransition = ({ children, tabKey }) => (
  <motion.div
    key={tabKey}
    initial={{ opacity: 0, y: 4 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.16, ease: 'easeOut' }}
  >
    {children}
  </motion.div>
);

/* -- chart colours ---------------------------------------------------------- */

const CHART_FALLBACK = {
  blue: 'oklch(0.62 0.17 255)',
  cmp: 'oklch(0.66 0.14 80)',
  chart2: 'oklch(0.70 0.12 230)',
  chart4: 'oklch(0.55 0.06 250)',
  grid: 'oklch(0.28 0.015 250)',
  text: 'oklch(0.58 0.014 250)',
  surface: 'oklch(0.18 0.014 250)',
  success: 'oklch(0.70 0.13 155)',
  danger: 'oklch(0.64 0.19 25)',
  accent: 'oklch(0.79 0.145 82)',
};

const TOKEN_MAP = {
  blue: '--adm-blue',
  cmp: '--adm-chart-cmp',
  chart2: '--adm-chart-2',
  chart4: '--adm-chart-4',
  grid: '--adm-border',
  text: '--adm-text-lo',
  surface: '--adm-bg-1',
  success: '--adm-success',
  danger: '--adm-danger',
  accent: '--adm-accent',
};

const readChartColors = () => {
  if (typeof document === 'undefined') return CHART_FALLBACK;
  const root = document.querySelector('.pp-admin');
  if (!root) return CHART_FALLBACK;
  const cs = getComputedStyle(root);
  const out = {};
  for (const [key, token] of Object.entries(TOKEN_MAP)) {
    const raw = cs.getPropertyValue(token).trim();
    out[key] = raw ? `oklch(${raw})` : CHART_FALLBACK[key];
  }
  return out;
};

/**
 * Recharts needs literal colour strings (SVG presentation attributes don't
 * resolve var()), so the tokens are read off the admin root and re-read when
 * the theme attribute flips.
 */
export const useAdmChartColors = () => {
  const [colors, setColors] = useState(CHART_FALLBACK);

  useEffect(() => {
    const root = document.querySelector('.pp-admin');
    setColors(readChartColors());
    if (!root) return undefined;
    const observer = new MutationObserver(() => setColors(readChartColors()));
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return colors;
};

/** Direct-labelled legend dot: colour lives in the dot, never in the text. */
export const ChartLegend = ({ series = [], className = '' }) => (
  <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 ${className}`}>
    {series.map((s) => (
      <span key={s.label} className="inline-flex items-center gap-1.5 text-xs text-adm-text-mid">
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ background: s.color }}
          aria-hidden="true"
        />
        {s.label}
        {s.dashed && <span className="text-adm-text-lo">(prior)</span>}
      </span>
    ))}
  </div>
);
